// TMOS Enter Actuals Page
// Date: January 13, 2026
// Purpose: Form for entering actual KPI measurement values

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { measurementService, CreateMeasurementRequest } from '@/services/measurementService';
import { planService } from '@/services/planService';
import { objectiveService } from '@/services/objectiveService';
import { Plan } from '@/types/plan';
import { PlanObjective } from '@/types/plan';
import toast from 'react-hot-toast';
import api from '@/services/api';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

interface KPI {
  id: string;
  name: string;
  unit?: string;
}

export const EnterActualsPage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Pre-fill from URL params
  const planIdParam = searchParams.get('plan_id');
  const objectiveIdParam = searchParams.get('objective_id');
  const kpiIdParam = searchParams.get('kpi_id');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState<CreateMeasurementRequest>({
    kpi_id: kpiIdParam || '',
    measurement_date: new Date().toISOString().split('T')[0],
    value: 0,
    plan_id: planIdParam || undefined,
    plan_objective_id: objectiveIdParam || undefined
  });

  // Options for dropdowns
  const [plans, setPlans] = useState<Plan[]>([]);
  const [objectives, setObjectives] = useState<PlanObjective[]>([]);
  const [kpis, setKpis] = useState<KPI[]>([]);
  const [selectedKpi, setSelectedKpi] = useState<KPI | null>(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (formData.plan_id) {
      loadObjectivesForPlan(formData.plan_id);
    }
  }, [formData.plan_id]);

  useEffect(() => {
    if (formData.plan_objective_id) {
      loadKpiForObjective(formData.plan_objective_id);
    }
  }, [formData.plan_objective_id]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // Load plans
      const plansData = await planService.getAllPlans();
      setPlans(plansData);
      
      if (planIdParam) {
        await loadObjectivesForPlan(planIdParam);
      }

      // Load KPIs
      const kpisResponse = await api.get<{ success: boolean; data: KPI[] }>('/kpis');
      if (kpisResponse.data.success) {
        setKpis(kpisResponse.data.data);
      }

      if (kpiIdParam) {
        const kpi = kpisResponse.data.data.find((k: KPI) => k.id === kpiIdParam);
        if (kpi) {
          setSelectedKpi(kpi);
        }
      }
    } catch (error: any) {
      console.error('Error loading initial data:', error);
      toast.error(error.message || t('businessPerformance.errorLoadingData') || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadObjectivesForPlan = async (planId: string) => {
    try {
      const objectivesData = await objectiveService.getObjectivesByPlan(planId);
      setObjectives(objectivesData);
      
      if (objectiveIdParam) {
        const objective = objectivesData.find(o => o.id === objectiveIdParam);
        if (objective && objective.kpi_id) {
          setFormData(prev => ({ ...prev, kpi_id: objective.kpi_id }));
          await loadKpiForObjective(objectiveIdParam);
        }
      }
    } catch (error: any) {
      console.error('Error loading objectives:', error);
      toast.error(error.message || 'Failed to load objectives');
    }
  };

  const loadKpiForObjective = async (objectiveId: string) => {
    try {
      const objective = await objectiveService.getObjectiveById(objectiveId);
      if (objective && objective.kpi_id) {
        const kpi = kpis.find(k => k.id === objective.kpi_id);
        if (kpi) {
          setSelectedKpi(kpi);
          setFormData(prev => ({ ...prev, kpi_id: objective.kpi_id }));
        }
      }
    } catch (error: any) {
      console.error('Error loading objective:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.kpi_id || !formData.measurement_date || formData.value === undefined) {
      toast.error(t('businessPerformance.requiredFieldsMissing') || 'Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      await measurementService.createMeasurement(formData);
      toast.success(t('businessPerformance.measurementCreated') || 'Measurement created successfully');
      
      // Navigate back or to dashboard
      if (formData.plan_id) {
        navigate(`/performance/plans/${formData.plan_id}`);
      } else if (formData.plan_objective_id) {
        navigate(`/performance/objectives/${formData.plan_objective_id}${formData.plan_id ? `?plan_id=${formData.plan_id}` : ''}`);
      } else {
        navigate('/performance');
      }
    } catch (error: any) {
      console.error('Error creating measurement:', error);
      toast.error(error.message || t('businessPerformance.errorCreatingMeasurement') || 'Failed to create measurement');
    } finally {
      setSaving(false);
    }
  };

  const handlePlanChange = (planId: string) => {
    setFormData(prev => ({ ...prev, plan_id: planId, plan_objective_id: undefined }));
    setObjectives([]);
  };

  const handleObjectiveChange = (objectiveId: string) => {
    setFormData(prev => ({ ...prev, plan_objective_id: objectiveId }));
    const objective = objectives.find(o => o.id === objectiveId);
    if (objective && objective.kpi_id) {
      setFormData(prev => ({ ...prev, kpi_id: objective.kpi_id }));
      const kpi = kpis.find(k => k.id === objective.kpi_id);
      if (kpi) {
        setSelectedKpi(kpi);
      }
    }
  };

  const handleKpiChange = (kpiId: string) => {
    const kpi = kpis.find(k => k.id === kpiId);
    setSelectedKpi(kpi || null);
    setFormData(prev => ({ ...prev, kpi_id: kpiId }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-secondary-bg)]">
        <div className="container mx-auto px-2 py-2">
          <div className="flex items-center justify-center h-64">
            <div className="text-[var(--color-secondary-text)]">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="container mx-auto px-2 py-2">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/performance')}
            className="mb-4 flex items-center gap-2"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            {t('common.back') || 'Back'}
          </Button>
          <h1 className="text-3xl font-bold text-[var(--color-surface-text)] mb-2">
            {t('businessPerformance.enterActuals') || 'Enter Actual Values'}
          </h1>
          <p className="text-[var(--color-secondary-text)]">
            {t('businessPerformance.enterActualsDescription') || 'Record actual KPI measurement values'}
          </p>
        </div>

        {/* Form */}
        <Card variant="outline">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Plan Selection */}
              <div>
                <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                  {t('businessPerformance.plan') || 'Plan'} <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.plan_id || ''}
                  onChange={(e) => handlePlanChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-[var(--color-surface-text)]"
                >
                  <option value="">{t('common.select') || 'Select a plan'}...</option>
                  {plans.map(plan => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} ({plan.plan_type} {plan.plan_year || ''})
                    </option>
                  ))}
                </select>
              </div>

              {/* Objective Selection */}
              {formData.plan_id && (
                <div>
                  <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                    {t('businessPerformance.objective') || 'Objective'} <span className="text-gray-400">({t('common.optional') || 'Optional'})</span>
                  </label>
                  <select
                    value={formData.plan_objective_id || ''}
                    onChange={(e) => handleObjectiveChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-[var(--color-surface-text)]"
                  >
                    <option value="">{t('common.select') || 'Select an objective'}...</option>
                    {objectives.map(objective => (
                      <option key={objective.id} value={objective.id}>
                        {objective.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* KPI Selection */}
              <div>
                <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                  {t('businessPerformance.kpi') || 'KPI'} <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.kpi_id}
                  onChange={(e) => handleKpiChange(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-[var(--color-surface-text)]"
                >
                  <option value="">{t('common.select') || 'Select a KPI'}...</option>
                  {kpis.map(kpi => (
                    <option key={kpi.id} value={kpi.id}>
                      {kpi.name} {kpi.unit ? `(${kpi.unit})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Measurement Date */}
              <div>
                <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                  {t('businessPerformance.measurementDate') || 'Measurement Date'} <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.measurement_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, measurement_date: e.target.value }))}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-[var(--color-surface-text)]"
                />
              </div>

              {/* Actual Value */}
              <div>
                <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                  {t('businessPerformance.actualValue') || 'Actual Value'} <span className="text-red-500">*</span>
                  {selectedKpi?.unit && (
                    <span className="text-gray-400 ml-2">({selectedKpi.unit})</span>
                  )}
                </label>
                <input
                  type="number"
                  step="any"
                  value={formData.value}
                  onChange={(e) => setFormData(prev => ({ ...prev, value: parseFloat(e.target.value) || 0 }))}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-[var(--color-surface-text)]"
                  placeholder="0.00"
                />
              </div>

              {/* Target Value (Optional) */}
              <div>
                <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                  {t('businessPerformance.targetValue') || 'Target Value'} <span className="text-gray-400">({t('common.optional') || 'Optional'})</span>
                  {selectedKpi?.unit && (
                    <span className="text-gray-400 ml-2">({selectedKpi.unit})</span>
                  )}
                </label>
                <input
                  type="number"
                  step="any"
                  value={formData.target_value || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, target_value: e.target.value ? parseFloat(e.target.value) : undefined }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-[var(--color-surface-text)]"
                  placeholder="0.00"
                />
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-4 pt-4 border-t border-gray-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/performance')}
                >
                  {t('common.cancel') || 'Cancel'}
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={saving}
                  className="flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <ArrowPathIcon className="w-4 h-4 animate-spin" />
                      {t('common.saving') || 'Saving'}...
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="w-4 h-4" />
                      {t('common.save') || 'Save'}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

